/** «Себестоимость» (до 22.10.2026 — «Закупочные цены»; `/purchase-prices`, право `purchase_prices`): ставка склада, фиксации по
 *  расчёту производства, ручные/контрактные цены, xlsx, продажи без цены. Источник правды —
 *  `docs/purchase-prices.md`. Адрес: `?tab=prices|missing|fixations`, `?fixation=<id>` — экран фиксации. */
import { ArrowLeft, Plus } from "lucide-react";
import React, { useState } from "react";

import type { FixationKind } from "@/api/purchasePrices";
import { Button } from "@/components/ui/button";
import { useUrlParam } from "@/lib/url-state";
import { cn } from "@/lib/utils";
import { FixationStartDialog } from "./FixationStart";
import { ActiveFixationBanner, FixationsList, FixationView } from "./Fixations";
import { MissingTab } from "./MissingTab";
import { AddPriceDialog } from "./PriceDialogs";
import { PricesTab } from "./PricesTab";
import { WarehouseRateBar } from "./WarehouseRate";
import { XlsxButtons } from "./XlsxButtons";

const TABS = [
  { id: "prices", label: "Цены" },
  { id: "missing", label: "Без цены" },
  { id: "fixations", label: "Фиксации" },
] as const;
type TabId = (typeof TABS)[number]["id"];

export function PurchasePricesPage(): React.ReactElement {
  const [tabParam, setTab] = useUrlParam("tab");
  const [fixParam, setFix] = useUrlParam("fixation");
  const [starting, setStarting] = useState<FixationKind | null>(null);
  const [adding, setAdding] = useState(false);
  const tab: TabId = TABS.some((t) => t.id === tabParam) ? (tabParam as TabId) : "prices";
  const fixationId = /^\d+$/.test(fixParam) ? Number(fixParam) : null;
  const openFixation = (id: number): void => setFix(String(id));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <h1 className="text-xl font-semibold">Себестоимость</h1>
        <WarehouseRateBar />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={() => setStarting("quarter")}>
          Зафиксировать квартал
        </Button>
        <Button size="sm" variant="outline" onClick={() => setStarting("new_items")}>
          Догрузить новинки
        </Button>
        <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
          <Plus /> Добавить цену
        </Button>
        <XlsxButtons />
      </div>

      {fixParam && fixationId === null && (
        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
          В адресе неверный номер фиксации: «{fixParam}».
        </p>
      )}
      {fixationId !== null ? (
        <div className="space-y-3">
          <Button size="sm" variant="ghost" onClick={() => setFix("")}>
            <ArrowLeft /> К разделу
          </Button>
          <FixationView key={fixationId} id={fixationId} />
        </div>
      ) : (
        <>
          <ActiveFixationBanner onOpen={openFixation} />
          <div role="tablist" className="flex gap-1 border-b">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={tab === t.id}
                onClick={() => setTab(t.id === "prices" ? "" : t.id)}
                className={cn(
                  "-mb-px border-b-2 px-3 py-1.5 text-sm font-medium",
                  tab === t.id
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground",
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
          {tab === "prices" && <PricesTab />}
          {tab === "missing" && <MissingTab />}
          {tab === "fixations" && <FixationsList onOpen={openFixation} />}
        </>
      )}

      {starting && (
        <FixationStartDialog
          kind={starting}
          onClose={() => setStarting(null)}
          onStarted={(id) => {
            setStarting(null);
            openFixation(id);
          }}
        />
      )}
      {adding && <AddPriceDialog preset={null} onClose={() => setAdding(false)} />}
    </div>
  );
}
